/**
 * 会话判断工具函数
 * 用于判断会话类型（陌生人、特殊会话等）
 */

import { SPECIAL_CONVERSATIONS } from "@/common/constants"
import { keyBy } from "lodash"
import { encrypt } from './crypto'
import { MINUTE_OF_MS, HOUR_OF_MS, DAY_OF_MS, WEEK_OF_MS } from '@/common/constants'

const specialConvTargetUidMap = keyBy(SPECIAL_CONVERSATIONS, 'target_uid')
const specialIds = SPECIAL_CONVERSATIONS.map(conv => conv.target_uid)

/**
 * 根据两个用户ID生成会话ID
 * 格式: encrypt(minMemberId_maxMemberId)
 * @param {string|number} memberId1 - 第一个用户ID
 * @param {string|number} memberId2 - 第二个用户ID
 * @returns {string} 加密后的会话ID
 */
export function generateConversationId(memberId1, memberId2) {
  // 使用较小的ID在前，确保一致性
  const [minId, maxId] = [memberId1, memberId2].sort()

  return encrypt(`${minId}_${maxId}`)
}

/**
 * 判断一个会话是否是陌生人会话 ✅
 *
 * 陌生人会话定义：同时满足以下所有条件
 * 1. 最后消息时间超过12小时
 * 2. 亲密度为0
 * 3. 聊天深度为0
 * 4. 非置顶
 * 5. 非系统特殊会话
 *
 * @param {Object} conversation - 会话对象
 * @param {number} conversation.last_msg_at - 最后消息时间戳（毫秒）
 * @param {number} conversation.intimacy_value - 亲密值
 * @param {number} conversation.chat_depth - 聊天深度
 * @param {number} conversation.rank - 置顶标识（0=未置顶）
 * @param {string} conversation.conversation_id - 会话ID
 * @returns {boolean}
 */
export function isStrangerConversation(conversation) {
  if (!conversation) {
    return false
  }

  // 排除特殊会话（系统、客服、团队）
  if (isSpecialConversation(conversation)) {
    return false
  }

  const now = Date.now()
  const twelveHours = 12 * HOUR_OF_MS

  // 使用空值合并运算符提供默认值，防止字段缺失导致判断错误
  const intimacyValue = conversation.intimacy_value ?? 0
  const chatDepth = conversation.chat_depth ?? 0
  const rank = conversation.rank ?? 0
  const lastMsgAt = conversation.last_msg_at ?? Date.now()

  // 条件检查
  const isTimedOut = (now - lastMsgAt) > twelveHours       // 超过12小时
  const isZeroIntimacy = intimacyValue === 0                // 亲密度为0
  const isZeroChatDepth = chatDepth <= 1                   // 聊天深度小于等于1
  const isNotPinned = rank === 0                             // 非置顶

  // 所有条件都满足才是陌生人会话
  return isTimedOut && isZeroIntimacy && isZeroChatDepth && isNotPinned
}

/**
 * 判断是否是特殊会话（系统、客服、团队、陌生人聚合）
 * @param {string} conversation - 会话
 * @returns {boolean}
 */
export function isSpecialConversation(conversation) {
  return specialIds.includes(conversation.target_uid)
}

/**
 * 获取特殊会话类型
 * @param {string} conversationId - 会话ID
 * @returns {string|null} 特殊会话类型或 null
 */
export function getSpecialConversationType(conversationId) {
  const typeMap = {
    SYSTEM_MESSAGE: 'system',
    CUSTOMER_SERVICE: 'customer',
    TEAM_MESSAGE: 'team',
    STRANGER_MESSAGE: 'stranger_aggregation'
  }

  return typeMap[conversationId] || null
}

/**
 * 判断会话是否应该显示在陌生人列表中
 * @param {Object} conversation - 会话对象
 * @returns {boolean}
 */
export function shouldShowInStrangerList(conversation) {
  if (!conversation) {
    return false
  }

  // 必须是陌生人会话
  if (!conversation.is_stranger) {
    return false
  }

  // 排除特殊会话（不在陌生人列表中显示）
  return !isSpecialConversation(conversation)
}

/**
 * 格式化会话列表，给陌生人会话、系统会话等打标记、替换头像等 ✅
 * @private
 * @param {Array} conversations - 原始会话列表
 * @param {Function} callbackFn - 回调函数，用于处理会话对象
 * @returns {Array} 格式化后的会话列表
 */
export function formatConversations(conversations, callbackFn = v => v) {
  if (!conversations || conversations.length === 0) {
    return []
  }
  return conversations.map(conv => {
    const { rank, target_uid } = conv
    let res;
    if (specialConvTargetUidMap[target_uid]) {
      const { target_nick_name, target_avatar_url, preview, rank, is_special } = specialConvTargetUidMap[target_uid]
      res = {
        ...conv,
        target_nick_name,
        target_avatar_url,
        rank,
        is_special,
        preview: conv.preview || preview,
      }
    } else {
      res = {
        ...conv,
        rank: rank !== undefined ? rank : 0
      }
    }
    return callbackFn(res)
  })
}

/**
 * 相对时间显示（如：刚刚、5分钟前、1小时前）
 * @param {number} timestamp - 时间戳（毫秒）
 * @returns {string} 相对时间字符串
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return ''

  const now = Date.now()
  const diff = now - timestamp

  if (diff < MINUTE_OF_MS) {
    return 'Just now'
  } else if (diff < HOUR_OF_MS) {
    const minutes = Math.floor(diff / MINUTE_OF_MS)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else if (diff < DAY_OF_MS) {
    const hours = Math.floor(diff / HOUR_OF_MS)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else if (diff < WEEK_OF_MS) {
    const days = Math.floor(diff / DAY_OF_MS)
    return `${days} day${days > 1 ? 's' : ''} ago`
  } else {
    // 超过一周，显示具体日期
    return formatConversationTime(timestamp)
  }
}

/**
 * 格式化会话列表的时间显示
 * @param {number} timestamp - 时间戳（毫秒）
 * @returns {string} 格式化后的时间字符串
 */
export function formatConversationTime(timestamp) {
  if (!timestamp) return ''

  const now = Date.now()
  const diff = now - timestamp

  const date = new Date(timestamp)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // 今天：显示时间
  if (date >= today) {
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  // 昨天
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date >= yesterday) {
    return 'Yesterday'
  }

  // 一周内：显示星期
  if (diff < 7 * DAY_OF_MS) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[date.getDay()]
  }

  // 更早：显示日期
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${month}/${day}`
}
